import type { TechniqueScrollRarity } from "../rarity.js";

export interface TechniqueProgressionCost {
  resultingLevel: number;
  scrollRarity: TechniqueScrollRarity;
  scrollQuantity: number;
  ryo: number;
  energy: number;
}

/** Canonical costs are retained even while the MVP resource charge is disabled. */
export const TECHNIQUE_PROGRESSION_COSTS = [
  {
    resultingLevel: 1,
    scrollRarity: "uncommon",
    scrollQuantity: 1,
    ryo: 250,
    energy: 50,
  },
  {
    resultingLevel: 2,
    scrollRarity: "uncommon",
    scrollQuantity: 1,
    ryo: 400,
    energy: 100,
  },
  {
    resultingLevel: 3,
    scrollRarity: "uncommon",
    scrollQuantity: 2,
    ryo: 750,
    energy: 200,
  },
  {
    resultingLevel: 4,
    scrollRarity: "uncommon",
    scrollQuantity: 3,
    ryo: 1_250,
    energy: 300,
  },
  {
    resultingLevel: 5,
    scrollRarity: "rare",
    scrollQuantity: 1,
    ryo: 2_000,
    energy: 500,
  },
  {
    resultingLevel: 6,
    scrollRarity: "rare",
    scrollQuantity: 2,
    ryo: 3_000,
    energy: 750,
  },
  {
    resultingLevel: 7,
    scrollRarity: "rare",
    scrollQuantity: 3,
    ryo: 4_500,
    energy: 1_100,
  },
  {
    resultingLevel: 8,
    scrollRarity: "epic",
    scrollQuantity: 1,
    ryo: 6_500,
    energy: 1_600,
  },
  {
    resultingLevel: 9,
    scrollRarity: "epic",
    scrollQuantity: 2,
    ryo: 9_000,
    energy: 2_400,
  },
  {
    resultingLevel: 10,
    scrollRarity: "legendary",
    scrollQuantity: 1,
    ryo: 15_000,
    energy: 4_000,
  },
] as const satisfies readonly TechniqueProgressionCost[];

export const TECHNIQUE_RESOURCE_COSTS_ENABLED = false;

export interface TechniqueProgressionRequirement {
  currentLevel: number | null;
  targetLevel: number;
  scrollRarity: TechniqueScrollRarity;
  scrollQuantity: number;
  canonicalRyo: number;
  canonicalEnergy: number;
  ryo: number;
  energy: number;
}

/** Returns null for a fully mastered technique; null current level means learn at Lv1. */
export function techniqueProgressionRequirement(
  currentLevel: number | null,
): TechniqueProgressionRequirement | null {
  if (
    currentLevel !== null &&
    (!Number.isInteger(currentLevel) || currentLevel < 1 || currentLevel > 10)
  ) {
    throw new RangeError(
      "Technique level must be null or an integer between 1 and 10.",
    );
  }
  const targetLevel = (currentLevel ?? 0) + 1;
  const cost = TECHNIQUE_PROGRESSION_COSTS[targetLevel - 1];
  if (!cost) return null;
  return {
    currentLevel,
    targetLevel,
    scrollRarity: cost.scrollRarity,
    scrollQuantity: cost.scrollQuantity,
    canonicalRyo: cost.ryo,
    canonicalEnergy: cost.energy,
    ryo: TECHNIQUE_RESOURCE_COSTS_ENABLED ? cost.ryo : 0,
    energy: TECHNIQUE_RESOURCE_COSTS_ENABLED ? cost.energy : 0,
  };
}

/** Returns the scroll rarity required to upgrade a technique from this level. */
export function nextTechniqueScrollRarity(
  currentLevel: number,
): TechniqueScrollRarity | null {
  if (
    !Number.isInteger(currentLevel) ||
    currentLevel < 1 ||
    currentLevel > 10
  ) {
    throw new RangeError(
      "Technique level must be an integer between 1 and 10.",
    );
  }
  if (currentLevel === 10) return null;

  const resultingLevel = currentLevel + 1;
  if (resultingLevel <= 4) return "uncommon";
  if (resultingLevel <= 7) return "rare";
  if (resultingLevel <= 9) return "epic";
  return "legendary";
}
